import React, { useState } from "react";
import { useModalContext } from "components/modal";
import { IAppointmentType, IAppointmentTypeDefault } from "../types/IAppointmentType";
import { AppointmentType } from "./AppointmentType";
import { AppointmentRow } from "./AppointmentRow";
import { EditAppointmentType } from "./EditAppointmentType";
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";
import { FaPlus } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { Button } from "components/button";

type Props = {
  appointmentTypes: IAppointmentType[];
};

const AppointmentTypeList = ({ appointmentTypes }: Props) => {
  const [showArchived, setShowArchived] = useState(false);
  const [expandedTypes, setExpandedTypes] = useState<Record<number, boolean>>({});
  const { setModal } = useModalContext();

  const { t } = useTranslation();
  const tListBase = "views.admin.appointment-types.components.AppointmentTypeList";
  const tTypeBase = "views.admin.appointment-types.components.AppointmentType";
  const trList = (key: string) => t(`${tListBase}.${key}`);
  const trType = (key: string) => t(`${tTypeBase}.${key}`);

  const activeAppointmentTypes = appointmentTypes.filter(
    (appointmentType) => appointmentType.active
  );

  const archivedAppointmentTypes = appointmentTypes.filter(
    (appointmentType) => !appointmentType.active
  );

  const toggleExpanded = (id: number) => {
    setExpandedTypes((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleClickEditDefault = (
    appointmentType: IAppointmentType,
    defaultItem: IAppointmentTypeDefault
  ) => {
    setModal({
      label: "Edit Appointment Sub-Type",
      component: (
        <EditAppointmentType
          type="child"
          mode="edit"
          appointmentType={appointmentType}
          defaultItem={defaultItem}
          appointmentTypeId={appointmentType.id}
          parentColor={appointmentType.color}
        />
      ),
    });
  };

  const renderDefaultRows = (appointmentType: IAppointmentType) => {
    const activeDefaults = (appointmentType.defaults || []).filter((defaultItem) => defaultItem.active);

    if (!activeDefaults.length) {
      return null;
    }

    return (
      <div className="border border-t-0 rounded-b-md bg-gray-50 p-3">
        <div className="space-y-2">
          {activeDefaults.map((defaultItem) => (
            <AppointmentRow
              key={defaultItem.id}
              id={defaultItem.id}
              label={defaultItem.label}
              color={defaultItem.color || appointmentType.color}
              nested={true}
              editLabel={trType("Edit")}
              onEdit={() => handleClickEditDefault(appointmentType, defaultItem)}
            />
          ))}
        </div>
      </div>
    );
  };

  return (
    <div>
      {/* Active Appointment Types */}
      <div>
        {activeAppointmentTypes?.map((appointmentType: IAppointmentType) => (
          <div key={appointmentType.id}>
            <AppointmentType
              appointmentType={appointmentType}
              isExpanded={expandedTypes[appointmentType.id] || false}
              onToggleExpand={() => toggleExpanded(appointmentType.id)}
            />
            {expandedTypes[appointmentType.id] ? renderDefaultRows(appointmentType) : null}
          </div>
        ))}
      </div>

      {/* Archived Section */}
      {archivedAppointmentTypes.length > 0 && (
        <div className="pt-3">
          <div className="flex items-center space-x-2">
            <h1>{trList("Archived")}</h1>
            <button
              onClick={() => setShowArchived(!showArchived)}
              className="flex items-center"
            >
              {showArchived ? (
                <IoIosArrowDown size={20} className="hover:text-primary" />
              ) : (
                <IoIosArrowUp size={20} className="hover:text-primary" />
              )}
            </button>
          </div>
          <div>
            {!showArchived &&
              archivedAppointmentTypes?.map(
                (appointmentType: IAppointmentType) => (
                  <div key={appointmentType.id}>
                    <AppointmentType
                      appointmentType={appointmentType}
                      archived={true}
                      isExpanded={expandedTypes[appointmentType.id] || false}
                      onToggleExpand={() => toggleExpanded(appointmentType.id)}
                    />
                    {expandedTypes[appointmentType.id] ? renderDefaultRows(appointmentType) : null}
                  </div>
                )
              )}
          </div>
        </div>
      )}
      <div className="h-[100px]"></div>
    </div>
  );
};

export { AppointmentTypeList };
